// Replace your existing form block with this controlled form
import React, { useMemo, useState } from "react";

function MortgageCalculatorForm() {
  const MAX_PRINCIPAL = 100000000; // 100M
  const MAX_RATE = 99;             // 99%
  const MAX_YEARS = 99;            // 99 years

  // store raw numeric values for calculations
  const [principalRaw, setPrincipalRaw] = useState(0); // number (in dollars)
  const [principalDisplay, setPrincipalDisplay] = useState(""); // string with $ & commas
  const [principalError, setPrincipalError] = useState(""); // validation error

  const [rateRaw, setRateRaw] = useState(0); // annual percent (e.g. 4 for 4%)
  const [rateDisplay, setRateDisplay] = useState(""); // string like "4%" while typing
  const [rateError, setRateError] = useState(""); // validation error

  const [yearsRaw, setYearsRaw] = useState(""); // integer years as string for input
  const [yearsError, setYearsError] = useState(""); // validation error

  // helpers
  const formatCurrency = (num) => {
    if (!Number.isFinite(num)) return "$0";
    return "$" + Math.round(num).toLocaleString("en-US");
  };

  const formatCurrencyWithCents = (num) => {
    if (!Number.isFinite(num)) return "$0.00";
    return (
      "$" +
      Number(num).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  };

  // handlers
  const onPrincipalChange = (e) => {
    const raw = e.target.value;
    const digits = raw.replace(/[^0-9.]/g, "");

    if (digits === "") {
      setPrincipalRaw(0);
      setPrincipalDisplay("");
      setPrincipalError("");
      return;
    }

    let parsed = Number(digits);
    if (!Number.isFinite(parsed)) parsed = 0;

    // validation: not more than 100M
    if (parsed > MAX_PRINCIPAL) {
      parsed = MAX_PRINCIPAL;
      setPrincipalError(
        `Maximum allowed amount is ${formatCurrency(MAX_PRINCIPAL)}`
      );
    } else {
      setPrincipalError("");
    }

    setPrincipalRaw(parsed);
    const intPart = Math.floor(parsed);
    setPrincipalDisplay("$" + intPart.toLocaleString("en-US"));
  };

  const onRateChange = (e) => {
    const raw = e.target.value;
    const digits = raw.replace(/[^0-9.]/g, "");

    if (digits === "") {
      setRateRaw(0);
      setRateDisplay("");
      setRateError("");
      return;
    }

    let parsed = Number(digits);
    if (!Number.isFinite(parsed)) parsed = 0;

    // validation: max 99%
    if (parsed > MAX_RATE) {
      parsed = MAX_RATE;
      setRateError(`Maximum allowed rate is ${MAX_RATE}%`);
    } else {
      setRateError("");
    }

    setRateRaw(parsed);
    setRateDisplay(String(parsed) + "%");
  };

  const onYearsChange = (e) => {
    let digits = e.target.value.replace(/[^0-9]/g, "");

    if (digits === "") {
      setYearsRaw("");
      setYearsError("");
      return;
    }

    let parsed = Number(digits);
    if (!Number.isFinite(parsed)) parsed = 0;

    // validation: max 99 years
    if (parsed > MAX_YEARS) {
      parsed = MAX_YEARS;
      setYearsError(`Maximum allowed period is ${MAX_YEARS} years`);
    } else {
      setYearsError("");
    }

    setYearsRaw(String(parsed));
  };

  // EMI calculation (memoized)
  const monthlyEMI = useMemo(() => {
    const P = principalRaw;
    const annualRate = rateRaw;
    const years = Number(yearsRaw || 0);

    if (!P || P <= 0 || years <= 0) return null;

    const monthlyRate = annualRate / 100 / 12;
    const n = years * 12;

    let payment;
    if (monthlyRate === 0) {
      payment = P / n;
    } else {
      const r = monthlyRate;
      payment = (P * r) / (1 - Math.pow(1 + r, -n));
    }

    return Number.isFinite(payment) ? payment : null;
  }, [principalRaw, rateRaw, yearsRaw]);

  const canShowResult =
    principalRaw > 0 &&
    Number(yearsRaw) > 0 &&
    (rateDisplay !== "" || rateRaw >= 0);

  return (
    <div className="calculate-form bg-gray2 position-relative">
      <h3>How much do you need?</h3>
      <form
        onSubmit={(e) => {
          e.preventDefault();
        }}
        autoComplete="off"
      >
        {/* Total Amount */}
        <div className="form-group">
          <label htmlFor="total-amount">Total Amount</label>
          <input
            id="total-amount"
            name="total_amount"
            className="form-control"
            placeholder="$30,000"
            value={principalDisplay}
            onChange={onPrincipalChange}
            inputMode="numeric"
          />
          {principalError && (
            <small
              className="form-text"
              style={{ color: "#dc3545", fontSize: "0.85rem" }}
            >
              {principalError}
            </small>
          )}
        </div>

        {/* Interest Rate */}
        <div className="form-group">
          <label htmlFor="interest-rate">Interest Rate</label>
          <input
            id="interest-rate"
            name="interest_rate"
            className="form-control"
            placeholder="4%"
            value={rateDisplay}
            onChange={onRateChange}
            inputMode="decimal"
            // maxLength={3}
          />
          {rateError ? (
            <small
              className="form-text"
              style={{ color: "#dc3545", fontSize: "0.85rem" }}
            >
              {rateError}
            </small>
          ) : (
            <small className="form-text text-muted">
              Enter annual rate (e.g. 4 or 4%).
            </small>
          )}
        </div>

        {/* EMI Period */}
        <div className="form-group">
          <label htmlFor="mortgage-period">EMI Period (years)</label>
          <input
            id="mortgage-period"
            name="mortgage_period"
            className="form-control"
            placeholder="30"
            value={yearsRaw}
            onChange={onYearsChange}
            inputMode="numeric"
            // maxLength={2}
          />
          {yearsError && (
            <small
              className="form-text"
              style={{ color: "#dc3545", fontSize: "0.85rem" }}
            >
              {yearsError}
            </small>
          )}
        </div>

        {/* results */}
        <div className="form-group mt-3">
          {canShowResult && monthlyEMI !== null ? (
            <div
              className="alert alert-light"
              role="status"
              style={{
                background: "#fff",
                border: "1px solid #eee",
                margin: 0,
                display:"flex",
                justifyContent:"space-between",
                alignItems:"center",
                padding:"10px"
              }}
            >
              <div style={{ fontSize: "0.95rem", color: "#666" }}>
                Monthly EMI
              </div>
              <div style={{ fontSize: "1.5rem", fontWeight: 700, color:"#31b677" }}>
                {formatCurrencyWithCents(monthlyEMI)}
              </div>

              {/* <div
                style={{
                  marginTop: 6,
                  fontSize: "0.9rem",
                  color: "#555",
                }}
              >
                Principal: {principalDisplay || formatCurrency(principalRaw)} •
                Period: {yearsRaw} years • Rate: {rateRaw}% p.a.
              </div> */}
            </div>
          ) : (
            <div style={{ color: "#777", fontSize: "0.95rem" }}>
              Enter Amount, Interest Rate and Period to see the EMI.
            </div>
          )}
        </div>
      </form>
    </div>
  );
}

export default MortgageCalculatorForm;
